import React from 'react';
import Markdown from 'react-markdown';
import { KnownReferrer } from 'hooks/useKnownReferrer';
import { multiDestinationLogger } from 'App/Tracking/multiDestinationLogger';
import { appendReferrer, getFinalTarget } from './utils';

const MarkdownLink = ({
  children,
  href,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  if (!href) {
    return <a {...props}>{children}</a>;
  }

  const finalHref = href.includes('tel:')
    ? href
    : appendReferrer(href, KnownReferrer.Nova);

  const finalTarget = getFinalTarget(finalHref);

  return (
    <a
      {...props}
      href={finalHref}
      target={finalTarget}
      rel={finalTarget === '_blank' ? 'noopener noreferrer' : undefined}
      onClick={() => {
        void multiDestinationLogger({
          eventName: 'nova-url-clicked',
          properties: {
            url: href,
            source: 'ai-coach-chatbot-ui',
          },
        });
      }}
    >
      {children}
    </a>
  );
};

const commonHeadingStyles = { marginBottom: '16px' } as const;

type Props = {
  children: string;
};

const MarkdownMessage = ({ children }: Props) => (
  <Markdown
    components={{
      a: ({ node, ...props }) => (
        <MarkdownLink {...props}>{props.children}</MarkdownLink>
      ),
      h1: ({ node, ...props }) => (
        <h1 {...props} style={commonHeadingStyles}>
          {props.children}
        </h1>
      ),
      h2: ({ node, ...props }) => (
        <h2 {...props} style={commonHeadingStyles}>
          {props.children}
        </h2>
      ),
      h3: ({ node, ...props }) => (
        <h3 {...props} style={commonHeadingStyles}>
          {props.children}
        </h3>
      ),
    }}
    urlTransform={u => u} // required to ensure tel: links are passed down in href prop
  >
    {children}
  </Markdown>
);

export default MarkdownMessage;
